import * as Api from '@ViewModels';
import { AIConversationType, BrandImage, BrandingSettings } from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { AIPromptUsage } from '../../../../models/AIPromptTemplate';
import { AIPromptField, useAIPromptField } from '../../../../shared/components/ai/AIPromptField';
import {
	invalidateBrandingSettingsQueries,
	useApplyAdminLogoMutation,
	useBrandingSettingsMutation,
	useBrandingSettingsQuery,
} from '../../../../queries/AccountProfile';
import { invalidateAllAIConversationFilterQueries } from '../../../../queries/AIConversation/useAIConversationFilterQuery';
import { useImageAttachmentMutation } from '../../../../queries/Attachments/useImageAttachmentMutation';
import { AIAssistantDialog } from '../../../components-v2/ai/AIAssistantDialog';
import { HexColorRenderer } from '../../../components-v2/ai/AIConversationMessage/renderers';
import { Alert, AlertDescription } from '../../../components-v2/Alert';
import { Button } from '../../../components-v2/Button';
import { ColorPicker } from '../../../components-v2/ColorPicker';
import { FontPicker } from '../../../components-v2/FontPicker';
import { UnifiedImageDialog } from '../../../components-v2/UnifiedImageDialog';
import { baseStyleSheet } from '../../../styles/styles';
import { LoadingSpinner } from '../../LoadingSpinner';
import { TrashIcon } from '../../svgs/icons/TrashIcon';
import { styleSheet } from '../styles';

const DEFAULT_BRANDING: BrandingSettings = {};
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
// Matches the API's BrandColorsMaxCount; a save above it is rejected with a 400.
const MAX_BRAND_COLORS = 6;
const NEW_BRAND_COLOR = '#000000';
// The brand logo is kept separate from the account (admin) logo by design.
const BRAND_LOGO_NAME = 'Logo';
const BRAND_LOGO_ROLE = 'logo';

const isDesignatedLogo = (image: BrandImage) => image.role === BRAND_LOGO_ROLE;
// Older settings only named the logo "Logo", and the old editor allowed several images with that name.
const isLegacyLogo = (image: BrandImage) =>
	!image.role && image.name?.trim().toLowerCase() === BRAND_LOGO_NAME.toLowerCase();

// The API prefers a Role = "logo" image over one named "Logo".
const findBrandLogoIndex = (images: BrandImage[]) => {
	const designated = images.findIndex(isDesignatedLogo);
	return designated >= 0 ? designated : images.findIndex(isLegacyLogo);
};

// Drops every logo candidate, so removing or replacing the logo can't surface a leftover copy.
const withoutBrandLogo = (images: BrandImage[] = []) =>
	images.filter(image => !isDesignatedLogo(image) && !isLegacyLogo(image));

const pluralizeColors = (count: number) => `${count} ${count === 1 ? 'color' : 'colors'}`;

interface IBrandSettingsSectionProps {
	/** Optional impersonation context. If not provided, uses the current user session's context. */
	impersonationContext?: Api.IImpersonationContext;
}

const BrandSettingsSectionComponent: React.FC<IBrandSettingsSectionProps> = ({
	impersonationContext: propImpersonationContext,
}) => {
	const brandDescriptionId = React.useId();
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const impersonationContext = propImpersonationContext ?? userSession.impersonationContext;

	// Branding state
	const brandingQuery = useBrandingSettingsQuery({ impersonationContext });
	const brandingMutation = useBrandingSettingsMutation({
		onError: error => {
			errorMessages.pushApiError(error);
		},
		onSuccess: () => {
			setBrandingDirty(false);
			toast.success('Branding settings saved successfully');
		},
	});
	const [localBranding, setLocalBranding] = React.useState<BrandingSettings>(DEFAULT_BRANDING);
	const [brandingDirty, setBrandingDirty] = React.useState(false);
	const [uploadingImage, setUploadingImage] = React.useState(false);
	const [brandingAssistantOpen, setBrandingAssistantOpen] = React.useState(false);
	const [imageLibraryOpen, setImageLibraryOpen] = React.useState(false);
	const [waitingForAIRefresh, setWaitingForAIRefresh] = React.useState(false);
	const fileInputRef = React.useRef<HTMLInputElement>(null);

	// Image upload mutation
	const imageUploadMutation = useImageAttachmentMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			setUploadingImage(false);
		},
	});

	// Server-side copies the account logo into a new, independent brand-logo attachment and returns the updated settings.
	const applyAdminLogoMutation = useApplyAdminLogoMutation({
		onError: error => {
			errorMessages.pushApiError(error);
		},
		onSuccess: brandingSettings => {
			setLocalBranding(brandingSettings);
			setBrandingDirty(false);
			toast.success('Logo applied successfully');
		},
	});

	// The account (admin) logo, reused for blog headers, surveys, etc. It is a separate image from the brand logo.
	// When impersonating, read ONLY the impersonated account's logo — never fall back to the admin's own
	// (userSession) logo, or the button would appear for accounts that have no logo of their own.
	const isImpersonating = !!impersonationContext?.account?.id;
	const adminLogoUrl = isImpersonating
		? impersonationContext?.account?.preferences?.logo?.url
		: userSession.account?.preferences?.logo?.url;
	const images = localBranding.images || [];
	const brandLogoIndex = findBrandLogoIndex(images);
	const brandLogo = brandLogoIndex >= 0 ? images[brandLogoIndex] : undefined;
	const brandColors = localBranding.brandColors || [];
	const excessColorCount = Math.max(0, brandColors.length - MAX_BRAND_COLORS);

	// Sync branding query data to local state
	React.useEffect(() => {
		if (brandingQuery.data) {
			// Only sync server data to local state when:
			// 1. The user has no unsaved changes, OR
			// 2. We're explicitly refreshing after the AI dialog updated server data
			if (!brandingDirty || waitingForAIRefresh) {
				setLocalBranding(brandingQuery.data);
			}
			if (waitingForAIRefresh && !brandingQuery.isFetching) {
				setWaitingForAIRefresh(false);
				setBrandingDirty(false); // AI data is now authoritative; clear dirty flag
			}
		}
	}, [brandingQuery.data, brandingQuery.isFetching, waitingForAIRefresh, brandingDirty]);

	// Branding handlers
	const updateBrandingField = <K extends keyof BrandingSettings>(field: K, value: BrandingSettings[K]) => {
		setLocalBranding(prev => ({ ...prev, [field]: value }));
		setBrandingDirty(true);
	};

	const brandDescriptionField = useAIPromptField({
		onChange: value => updateBrandingField('brandDescription', value),
		value: localBranding.brandDescription || '',
	});

	const handleSaveBranding = () => {
		if (brandDescriptionField.unfilledPlaceholder || excessColorCount > 0) {
			return;
		}
		const designatedImages =
			brandLogo && brandLogo.role !== BRAND_LOGO_ROLE
				? images.map((image, index) => (index === brandLogoIndex ? { ...image, role: BRAND_LOGO_ROLE } : image))
				: localBranding.images;
		brandingMutation.mutate({
			brandingSettings: { ...localBranding, images: designatedImages },
			impersonationContext,
		});
	};

	const handleCancelBranding = () => {
		if (brandingQuery.data) {
			setLocalBranding(brandingQuery.data);
		} else {
			setLocalBranding(DEFAULT_BRANDING);
		}
		setBrandingDirty(false);
	};

	const handleClearAllBranding = () => {
		// Fields this section doesn't show (other images, ink color, headings font) can't be restored here, so keep them.
		const hiddenImages = withoutBrandLogo(images);
		setLocalBranding({
			...DEFAULT_BRANDING,
			inkColor: localBranding.inkColor,
			headingsFont: localBranding.headingsFont,
			brandColors: [],
			images: hiddenImages.length > 0 ? hiddenImages : undefined,
		});
		setBrandingDirty(true);
	};

	const handleAddColor = () => {
		updateBrandingField('brandColors', [...brandColors, NEW_BRAND_COLOR]);
	};

	const handleUpdateColor = (index: number, color: string) => {
		updateBrandingField(
			'brandColors',
			brandColors.map((existing, i) => (i === index ? color : existing))
		);
	};

	const handleRemoveColor = (index: number) => {
		updateBrandingField(
			'brandColors',
			brandColors.filter((_, i) => i !== index)
		);
	};

	const handleRemoveLogo = () => {
		const newImages = withoutBrandLogo(images);
		updateBrandingField('images', newImages.length > 0 ? newImages : undefined);
	};

	const setBrandLogo = (attachment: Api.IFileAttachment) => {
		const logo: BrandImage = {
			name: BRAND_LOGO_NAME,
			fileAttachmentId: attachment.id,
			description: '',
			fileAttachment: attachment,
			role: BRAND_LOGO_ROLE,
		};
		// Read the latest images: they can change while an upload is in flight.
		setLocalBranding(prev => ({ ...prev, images: [...withoutBrandLogo(prev.images), logo] }));
		setBrandingDirty(true);
	};

	const handleLibraryImageSelected = (attachment: Api.IFileAttachment) => {
		setImageLibraryOpen(false);
		setBrandLogo(attachment);
	};

	const handleUploadLogoClick = () => {
		fileInputRef.current?.click();
	};

	const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		// Reset the input so the same file can be selected again
		e.target.value = '';

		// Validate file type
		if (!file.type.startsWith('image/')) {
			toast.error('Please select an image file');
			return;
		}

		// Validate file size (5MB limit)
		if (file.size > MAX_IMAGE_SIZE_BYTES) {
			toast.error('Image must be less than 5MB');
			return;
		}

		setUploadingImage(true);

		try {
			const attachments = await imageUploadMutation.mutateAsync({
				files: [file],
				impersonationContext,
			});

			if (attachments && attachments.length > 0) {
				setBrandLogo(attachments[0]);
			}
		} catch {
			// Error already handled in mutation onError
		} finally {
			setUploadingImage(false);
		}
	};

	const handleApplyAdminLogo = () => {
		if (!adminLogoUrl) return;
		applyAdminLogoMutation.mutate({ impersonationContext });
	};

	// Branding AI Assistant handlers
	const handleBrandingAssistantOpenChange = React.useCallback((open: boolean) => {
		setBrandingAssistantOpen(open);
		if (!open) {
			// Show loading state while we wait for fresh data after AI dialog closes
			setWaitingForAIRefresh(true);
			// Refresh branding settings when dialog closes
			invalidateBrandingSettingsQueries();
		} else {
			invalidateAllAIConversationFilterQueries();
		}
	}, []);

	return (
		<div className='settings-admin-company-container'>
			<p className={css(styleSheet.subsectionDescription)}>
				Configure your brand colors, font, and logo to customize AI-generated content.
			</p>

			<div className={css(baseStyleSheet.mb3)}>
				<Button variant='secondary' size='sm' onClick={() => setBrandingAssistantOpen(true)}>
					Auto-Discover with AI
				</Button>
			</div>

			<AIAssistantDialog
				open={brandingAssistantOpen}
				onOpenChange={handleBrandingAssistantOpenChange}
				assistantName='BrandingAgent'
				conversationType={AIConversationType.Default}
				title='Brand Settings Discovery'
				autoStart
				placeholder='Tell me about your brand...'
				impersonationContext={impersonationContext}
				startNewConversation
				customRenderers={[HexColorRenderer]}
				supportsAttachments
			/>

			{brandingQuery.isPending ? (
				<LoadingSpinner type='small' />
			) : (
				<div className={css(styleSheet.brandingContentWrapper)}>
					{waitingForAIRefresh && (
						<div className={css(styleSheet.brandingLoadingOverlay)}>
							<LoadingSpinner type='small' />
						</div>
					)}
					<div className={css(styleSheet.brandingSubsection)}>
						<div className={css(styleSheet.brandingSubsectionTitle)}>Brand Colors</div>
						<p className={css(styleSheet.subsectionDescription)}>
							Optional. Up to {MAX_BRAND_COLORS} colors used for AI-generated images and templates.
						</p>
						{brandColors.length > 0 && (
							<ul className={css(styleSheet.brandingColorList)}>
								{brandColors.map((color, index) => (
									<li key={index} className={css(styleSheet.brandingColorWithClear)}>
										<ColorPicker value={color} onChange={newColor => handleUpdateColor(index, newColor)} />
										<Button
											variant='link'
											size='icon'
											onClick={() => handleRemoveColor(index)}
											aria-label={`Remove color ${color}`}
											title='Remove color'
										>
											<TrashIcon />
										</Button>
									</li>
								))}
							</ul>
						)}
						<div className={css(styleSheet.brandingImageActionsRow)}>
							<Button
								variant='secondary'
								size='sm'
								onClick={handleAddColor}
								disabled={brandColors.length >= MAX_BRAND_COLORS}
							>
								+ Add Color
							</Button>
							<span className={css(styleSheet.brandingColorLabel)}>
								{brandColors.length} of {MAX_BRAND_COLORS} colors
							</span>
						</div>
						{excessColorCount > 0 && (
							<Alert tone='warning' styles={[styleSheet.brandingAlert]}>
								<AlertDescription>
									You have {pluralizeColors(brandColors.length)}. Remove {pluralizeColors(excessColorCount)} to save;
									brand settings allow up to {MAX_BRAND_COLORS}.
								</AlertDescription>
							</Alert>
						)}
					</div>

					{/* Brand Font */}
					<div className={css(styleSheet.brandingSubsection)}>
						<div className={css(styleSheet.brandingSubsectionTitle)}>Brand Font</div>
						<div className={css(styleSheet.brandingFontSelect)}>
							<FontPicker
								value={{
									fontName: localBranding.font?.fontName || '',
									fontUri: localBranding.font?.fontUri || '',
								}}
								onChange={font =>
									updateBrandingField('font', {
										fontName: font.fontName,
										fontUri: font.fontUri,
									})
								}
							/>
						</div>
					</div>

					<div className={css(styleSheet.brandingSubsection)}>
						<div className={css(styleSheet.brandingSubsectionTitle)}>Logo</div>
						{brandLogo ? (
							brandLogo.fileAttachment?.url ? (
								<img
									src={brandLogo.fileAttachment.url}
									alt='Brand logo'
									className={css(styleSheet.brandingLogoPreview)}
								/>
							) : (
								<div className={css(styleSheet.brandingImagePlaceholder)}>Logo</div>
							)
						) : (
							<Alert tone='info' styles={[styleSheet.brandingAlert]}>
								<AlertDescription>
									Add your logo so your branding can be applied to templates and AI-generated content.
								</AlertDescription>
							</Alert>
						)}
						<input
							ref={fileInputRef}
							type='file'
							accept='image/*'
							style={{ display: 'none' }}
							onChange={handleFileSelected}
						/>
						<div className={css(styleSheet.brandingImageActionsRow)}>
							<Button variant='secondary' size='sm' onClick={handleUploadLogoClick} disabled={uploadingImage}>
								{uploadingImage ? (
									<>
										<LoadingSpinner type='small' /> Uploading...
									</>
								) : brandLogo ? (
									'Replace Logo'
								) : (
									'Upload Logo'
								)}
							</Button>
							<Button variant='secondary' size='sm' onClick={() => setImageLibraryOpen(true)} disabled={uploadingImage}>
								Choose from My Images
							</Button>
							{brandLogo && (
								<Button variant='link' size='sm' onClick={handleRemoveLogo} disabled={uploadingImage}>
									Remove Logo
								</Button>
							)}
							{!brandLogo && adminLogoUrl && (
								<Button
									variant='secondary'
									size='sm'
									onClick={handleApplyAdminLogo}
									disabled={applyAdminLogoMutation.isPending || uploadingImage}
								>
									{applyAdminLogoMutation.isPending ? (
										<>
											<LoadingSpinner type='small' /> Applying...
										</>
									) : (
										'Apply existing logo'
									)}
								</Button>
							)}
						</div>
						<UnifiedImageDialog
							open={imageLibraryOpen}
							onOpenChange={setImageLibraryOpen}
							onImageSelected={handleLibraryImageSelected}
							impersonationContext={impersonationContext}
							initialTab='image-library'
							showImageLibraryTab
							showUploadTab={false}
							showFreeImagesTab={false}
							showAIGenerateTab={false}
						/>
					</div>

					{/* Brand Description */}
					<div className={css(styleSheet.brandingSubsection)}>
						<div className={css(styleSheet.brandingSubsectionTitle)} id={`${brandDescriptionId}-label`}>
							Brand Description
						</div>
						<p className={css(styleSheet.subsectionDescription)}>
							Describe your brand style and tone for AI-generated content.
						</p>
						<div className={css(styleSheet.brandingDescriptionField)}>
							<AIPromptField
								aria-labelledby={`${brandDescriptionId}-label`}
								field={brandDescriptionField}
								id={brandDescriptionId}
								impersonationContext={impersonationContext}
								placeholder='e.g., Professional and modern with a friendly tone. Use clean, minimalist designs with bold typography.'
								textAreaStyles={[styleSheet.brandingTextarea]}
								usage={AIPromptUsage.BrandVoice}
							/>
						</div>
					</div>

					{/* Branding Save/Cancel/Clear */}
					<div className={css(styleSheet.brandingButtonRow)}>
						<div className={css(styleSheet.buttonContainer)}>
							<Button
								variant='primary'
								size='sm'
								onClick={handleSaveBranding}
								disabled={
									!brandingDirty ||
									brandingMutation.isPending ||
									!!brandDescriptionField.unfilledPlaceholder ||
									excessColorCount > 0
								}
							>
								{brandingMutation.isPending ? 'Saving...' : 'Save'}
							</Button>
							<Button
								variant='secondary'
								size='sm'
								onClick={handleCancelBranding}
								disabled={!brandingDirty || uploadingImage}
							>
								Cancel
							</Button>
							<Button variant='secondary' size='sm' onClick={handleClearAllBranding} disabled={uploadingImage}>
								Clear
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export const BrandSettingsSection = React.memo(BrandSettingsSectionComponent);
